import React, { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

import "./SecondaryPillTabs.css";

const SecondaryPillTabs = ({ tabs = [], activeTab, onChange }) => {
  const [openMenuId, setOpenMenuId] = useState(null);

  const tabsRef = useRef(null);
  const menuRefs = useRef({});

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (tabsRef.current && !tabsRef.current.contains(event.target)) {
        setOpenMenuId(null);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;

      if (openMenuId !== null) {
        menuRefs.current[openMenuId]?.focus();
        setOpenMenuId(null);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [openMenuId]);

  useEffect(() => {
    setOpenMenuId(null);
  }, [activeTab]);

  const handleTabClick = (tab) => {
    if (tab.disabled) return;

    const hasMenu = Array.isArray(tab.menu) && tab.menu.length > 0;

    if (hasMenu) {
      setOpenMenuId((current) => (current === tab.id ? null : tab.id));
      return;
    }

    setOpenMenuId(null);
    onChange?.(tab.id, tab);
  };

  const handleMenuItemClick = (item) => {
    if (item.disabled) return;

    setOpenMenuId(null);
    onChange?.(item.id, item);
  };

  return (
    <nav
      ref={tabsRef}
      className="secondary-pill-tabs"
      aria-label="Secondary section tabs"
    >
      {tabs.map((tab) => {
        const hasMenu = Array.isArray(tab.menu) && tab.menu.length > 0;

        const isMenuActive =
          hasMenu && tab.menu.some((item) => item.id === activeTab);

        const isActive = activeTab === tab.id || isMenuActive;

        const isOpen = openMenuId === tab.id;

        return (
          <div key={tab.id} className="secondary-pill-tab-wrapper">
            <button
              ref={
                hasMenu
                  ? (node) => {
                      if (node) {
                        menuRefs.current[tab.id] = node;
                      } else {
                        delete menuRefs.current[tab.id];
                      }
                    }
                  : undefined
              }
              type="button"
              className={`secondary-pill-tab ${
                isActive ? "secondary-pill-tab--active" : ""
              }`}
              onClick={() => handleTabClick(tab)}
              disabled={tab.disabled}
              aria-current={isActive && !hasMenu ? "page" : undefined}
              aria-haspopup={hasMenu ? "menu" : undefined}
              aria-expanded={hasMenu ? isOpen : undefined}
            >
              {tab.icon && (
                <span className="secondary-pill-tab__icon">{tab.icon}</span>
              )}

              <span className="secondary-pill-tab__label">{tab.label}</span>

              {typeof tab.count !== "undefined" && (
                <span className="secondary-pill-tab__count">({tab.count})</span>
              )}

              {hasMenu && (
                <ChevronDown
                  size={15}
                  aria-hidden="true"
                  className={`secondary-pill-tab__chevron ${
                    isOpen ? "secondary-pill-tab__chevron--open" : ""
                  }`}
                />
              )}
            </button>

            {hasMenu && isOpen && (
              <div
                className="secondary-pill-tab__dropdown"
                role="menu"
                aria-label={`${tab.label} navigation`}
              >
                {tab.menu.map((item) => {
                  const isItemActive = activeTab === item.id;

                  return (
                    <button
                      key={item.id}
                      type="button"
                      role="menuitem"
                      className={`secondary-pill-tab__dropdown-item ${
                        isItemActive
                          ? "secondary-pill-tab__dropdown-item--active"
                          : ""
                      }`}
                      onClick={() => handleMenuItemClick(item)}
                      disabled={item.disabled}
                      aria-current={isItemActive ? "page" : undefined}
                    >
                      {item.icon && (
                        <span className="secondary-pill-tab__dropdown-icon">
                          {item.icon}
                        </span>
                      )}

                      <span>{item.label}</span>

                      {typeof item.count !== "undefined" && (
                        <span className="secondary-pill-tab__dropdown-count">
                          {item.count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
};

export default SecondaryPillTabs;
